const STORAGE_PREFIX = 'swiftuni:ielts-draft:';

const normalizeValue = (value) => String(value ?? '').trim().toLowerCase();

export function buildIeltsDraftKey({ scope, section, testId, groupId, fullTestAttemptId }) {
  const normalizedScope = normalizeValue(scope || (fullTestAttemptId ? 'full' : 'sectional')) || 'sectional';
  const normalizedSection = normalizeValue(section);
  if (!normalizedSection || !testId) return null;

  const parts = ['ielts', normalizedScope, normalizedSection];
  if (normalizedScope === 'full') {
    if (groupId) parts.push(`group-${groupId}`);
    if (fullTestAttemptId) parts.push(`full-${fullTestAttemptId}`);
  }
  parts.push(`test-${testId}`);
  return parts.join(':');
}

function storageKey(draftKey) {
  return draftKey ? `${STORAGE_PREFIX}${draftKey}` : null;
}

export function loadIeltsDraftFromBrowser(draftKey) {
  const key = storageKey(draftKey);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export function loadIeltsDraftStateFromBrowser(identity) {
  const draftKey = buildIeltsDraftKey(identity || {});
  const draft = loadIeltsDraftFromBrowser(draftKey);
  return draft?.state || null;
}

export function saveIeltsDraftToBrowser(draftKey, draft) {
  const key = storageKey(draftKey);
  if (!key) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(draft));
  } catch (_) {
    // Browser storage can be unavailable or full. Backend autosave still protects the attempt.
  }
}

export function removeIeltsDraftFromBrowser(draftKey) {
  const key = storageKey(draftKey);
  if (!key) return;
  try {
    window.localStorage.removeItem(key);
  } catch (_) {
    // Ignore local cleanup failures.
  }
}

function getBrowserStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch (_) {
    return null;
  }
}

function parseFullAttemptIdFromDraftKey(draftKey) {
  const match = String(draftKey || '').match(/:full-(\d+)(?=:|$)/);
  return match ? Number(match[1]) : null;
}

function getDraftMode(draft) {
  const mode = draft?.state?.currentStep?.mode || draft?.current_step?.mode;
  return mode === 'simulation' ? 'simulation' : 'practice';
}

export function findFullIeltsDraftSummaryFromBrowser({ groupId, tests = [] } = {}) {
  const storage = getBrowserStorage();
  if (!storage || !groupId) return null;

  const groupNeedle = `:group-${groupId}:`;
  const testIds = new Set(
    (tests || [])
      .map((test) => String(test?.id || test?.test_id || '').trim())
      .filter(Boolean)
  );

  let latest = null;
  for (let index = 0; index < storage.length; index += 1) {
    const storageName = storage.key(index);
    if (!storageName || !storageName.startsWith(STORAGE_PREFIX)) continue;

    const draftKey = storageName.slice(STORAGE_PREFIX.length);
    if (!draftKey.startsWith('ielts:full:') || !draftKey.includes(groupNeedle)) continue;

    const testMatch = draftKey.match(/:test-([^:]+)$/);
    if (testIds.size && (!testMatch || !testIds.has(String(testMatch[1])))) continue;

    const draft = loadIeltsDraftFromBrowser(draftKey);
    if (!draft) continue;

    const updatedAt = draft.updatedAt || draft.updated_at || draft.state?.currentStep?.updatedAt || null;
    const updatedTime = updatedAt ? new Date(updatedAt).getTime() : 0;
    if (!latest || updatedTime >= latest.updatedTime) {
      latest = {
        draftKey,
        fullTestAttemptId: parseFullAttemptIdFromDraftKey(draftKey),
        mode: getDraftMode(draft),
        updatedAt,
        updatedTime,
      };
    }
  }

  return latest;
}

export function removeFullIeltsDraftsFromBrowser({ groupId, tests = [], fullTestAttemptId } = {}) {
  const storage = getBrowserStorage();
  if (!storage || !groupId) return [];

  const groupNeedle = `:group-${groupId}:`;
  const fullNeedle = fullTestAttemptId ? `:full-${fullTestAttemptId}:` : null;
  const testIds = new Set(
    (tests || [])
      .map((test) => String(test?.id || test?.test_id || '').trim())
      .filter(Boolean)
  );

  const draftKeys = [];
  for (let index = 0; index < storage.length; index += 1) {
    const storageName = storage.key(index);
    if (!storageName || !storageName.startsWith(STORAGE_PREFIX)) continue;

    const draftKey = storageName.slice(STORAGE_PREFIX.length);
    if (!draftKey.startsWith('ielts:full:') || !draftKey.includes(groupNeedle)) continue;
    if (fullNeedle && !draftKey.includes(fullNeedle)) continue;

    const testMatch = draftKey.match(/:test-([^:]+)$/);
    if (testIds.size && (!testMatch || !testIds.has(String(testMatch[1])))) continue;

    draftKeys.push(draftKey);
  }

  draftKeys.forEach((draftKey) => removeIeltsDraftFromBrowser(draftKey));
  return draftKeys;
}
